// Web 版本：移除相册和相机功能
import { Platform } from 'react-native';
import { showToast } from '../components/Toast';
import { PictureResult } from '../picture-android/PictureResult';
import { showMsgDialog } from '../components/CommonDialog';
import { Str_MaxFileCountLimit } from '../language/string_res';

export type MediaType = 'video' | 'photo' | 'any';

export type PickerOption = {
	type: MediaType;
	multiple: boolean;
	maxNum: number;
	isCrop?: boolean;
	includeGif?: boolean;
};

export type PickerResult = {
	type: 'video' | 'photo' | 'doc';
	width: number;
	height: number;
	path: string;
	mimeType: string;
	thumbnailPath?: string; //视频 缩略图
	isHadCompressed?: boolean;
	fileName?: string; // 文件名，主要针对文档
	fileSize?: number; // 文件大小，主要针对文档
};

// Web 版本：打开文件选择器
function openWebFilePicker(type: MediaType): Promise<PickerResult | undefined> {
	return new Promise((resolve) => {
		if (Platform.OS !== 'web') {
			console.log('[openWebFilePicker] 非 Web 平台，返回 undefined');
			resolve(undefined);
			return;
		}

		console.log('[openWebFilePicker] 开始创建文件选择器，类型:', type);
		const input = document.createElement('input');
		input.type = 'file';
		// 根据类型限制文件选择
		if (type === 'video') {
			input.accept = 'video/*';
		} else if (type === 'photo') {
			input.accept = 'image/*';
		} else {
			// 'any' 类型：只支持图片和视频，不支持其他文件类型
			input.accept = 'image/*,video/*';
		}
		input.style.display = 'none';

		input.onchange = async (e: any) => {
			console.log('[openWebFilePicker] 文件选择事件触发');
			const file = e.target.files?.[0];
			if (!file) {
				console.log('[openWebFilePicker] 未选择文件');
				resolve(undefined);
				return;
			}

			console.log('[openWebFilePicker] 选择的文件:', file.name, '类型:', file.type, '大小:', file.size);

			// 判断文件类型
			const isVideo = file.type.startsWith('video/');
			const isImage = file.type.startsWith('image/');
			// 对于非图片/视频文件，归类为 photo（因为 PickerResult 只支持 'video' | 'photo'）
			const fileType: 'video' | 'photo' = isVideo ? 'video' : 'photo';

			// 创建 FileReader 读取文件
			const reader = new FileReader();

			reader.onload = (event: any) => {
				console.log('[openWebFilePicker] 文件读取成功，开始处理');
				const dataUrl = event.target.result;

				// 如果是图片，获取图片尺寸
				if (isImage) {
					// 使用 HTMLImageElement 而不是 React Native 的 Image
					const img = document.createElement('img');
					img.onload = () => {
						console.log('[openWebFilePicker] 图片加载成功，尺寸:', img.width, 'x', img.height);
						resolve({
							type: fileType,
							width: img.width,
							height: img.height,
							path: dataUrl,
							mimeType: file.type,
							isHadCompressed: false,
							fileName: file.name,
							fileSize: file.size,
						});
					};
					img.onerror = (error) => {
						console.warn('[openWebFilePicker] 图片加载失败:', error);
						// 如果无法读取图片尺寸，使用默认值
						resolve({
							type: fileType,
							width: 0,
							height: 0,
							path: dataUrl,
							mimeType: file.type,
							isHadCompressed: false,
							fileName: file.name,
							fileSize: file.size,
						});
					};
					img.src = dataUrl;
				} else if (isVideo) {
					// 视频文件：获取视频尺寸
					console.log('[openWebFilePicker] 视频文件，开始获取尺寸');
					const video = document.createElement('video');
					video.preload = 'metadata';
					video.crossOrigin = 'anonymous';

					// 设置超时，避免无限等待
					const timeout = setTimeout(() => {
						console.warn('[openWebFilePicker] 视频元数据加载超时');
						video.onloadedmetadata = null;
						video.onerror = null;
						resolve({
							type: fileType,
							width: 0,
							height: 0,
							path: dataUrl,
							mimeType: file.type,
							isHadCompressed: false,
							fileName: file.name,
							fileSize: file.size,
						});
					}, 10000); // 10秒超时

					video.onloadedmetadata = () => {
						clearTimeout(timeout);
						console.log('[openWebFilePicker] 视频加载成功，尺寸:', video.videoWidth, 'x', video.videoHeight);
						video.onloadedmetadata = null;
						video.onerror = null;
						resolve({
							type: fileType,
							width: video.videoWidth,
							height: video.videoHeight,
							path: dataUrl,
							mimeType: file.type,
							isHadCompressed: false,
							fileName: file.name,
							fileSize: file.size,

						});
					};

					video.onerror = (error) => {
						clearTimeout(timeout);
						console.warn('[openWebFilePicker] 视频加载失败:', error);
						video.onloadedmetadata = null;
						video.onerror = null;
						resolve({
							type: fileType,
							width: 0,
							height: 0,
							path: dataUrl,
							mimeType: file.type,
							isHadCompressed: false,
							fileName: file.name,
							fileSize: file.size,
						});
					};

					video.src = dataUrl;
				} else {
					// 其他文件类型
					console.log('[openWebFilePicker] 其他文件类型，直接返回');
					let mimeType = file.type;
					if (!mimeType && file.name) {
						const ext = file.name.split('.').pop()?.toLowerCase();
						const mimeMap: Record<string, string> = {
							pdf: 'application/pdf',
							doc: 'application/msword',
							docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
							xls: 'application/vnd.ms-excel',
							xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
							ppt: 'application/vnd.ms-powerpoint',
							pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
							txt: 'text/plain',
							zip: 'application/zip',
							rar: 'application/x-rar-compressed',
							csv: 'text/csv',
							jpg: 'image/jpeg',
							jpeg: 'image/jpeg',
							png: 'image/png',
							gif: 'image/gif',
							webp: 'image/webp',
							svg: 'image/svg+xml',
						};
						if (typeof console !== 'undefined') {
							console.error('[BEE-FILE][MIME-LOOKUP]', {
								fileName: file.name,
								ext,
								mimeTypeBefore: mimeType
							});
						}
						mimeType = mimeMap[ext || ''] || 'application/octet-stream';
						if (typeof console !== 'undefined') {
							console.error('[BEE-FILE][MIME-RESULT]', {
								fileName: file.name,
								ext,
								mimeTypeAfter: mimeType
							});
						}
					}
					resolve({
						type: fileType,
						width: 0,
						height: 0,
						path: dataUrl,
						mimeType,
						isHadCompressed: false,
						fileName: file.name,
						fileSize: file.size,
					});
				}
			};

			reader.onerror = (error) => {
				console.error('[openWebFilePicker] 文件读取失败:', error);
				resolve(undefined);
			};

			console.log('[openWebFilePicker] 开始读取文件为 Data URL');
			reader.readAsDataURL(file);
		};

		input.oncancel = () => {
			console.log('[openWebFilePicker] 用户取消选择');
			resolve(undefined);
		};

		document.body.appendChild(input);
		console.log('[openWebFilePicker] 触发文件选择器点击');
		input.click();

		// 清理
		setTimeout(() => {
			if (document.body.contains(input)) {
				document.body.removeChild(input);
			}
		}, 100);
	});
}

export async function openAndroidPicker(op: PickerOption): Promise<PickerResult[] | undefined> {
	if (Platform.OS === 'web') {
		console.warn('openAndroidPicker not supported on web, use openSinglePicker or openMultiplePicker instead');
		return undefined;
	}
	console.warn('openAndroidPicker not supported on web');
	return undefined;
}

export async function openAndroidCamera(type: 'video' | 'photo'): Promise<PictureResult | undefined> {
	console.warn('openAndroidCamera not supported on web');
	return undefined;
}

export async function openSinglePicker(
	type: MediaType,
	isCrop: boolean = false,
	includeGif: boolean = true
): Promise<PickerResult | undefined> {
	return openWebFilePicker(type);
}

export async function openMultiplePicker(type: MediaType, maxNum: number): Promise<PickerResult[] | undefined> {

	return new Promise((resolve) => {
		const input = document.createElement('input');
		input.type = 'file';
		// 根据类型限制文件选择
		if (type === 'video') {
			input.accept = 'video/*';
		} else if (type === 'photo') {
			input.accept = 'image/*';
		} else {
			// 'any' 类型：当前版本仅支持图片和视频（文档能力下个版本开放）
			input.accept = 'image/*,video/*';
		}
		input.multiple = true;
		input.style.display = 'none';

		input.onchange = async (e: any) => {
			const files = Array.from(e.target.files || []);
			if (files.length === 0) {
				resolve(undefined);
				return;
			}
			const results: PickerResult[] = [];
			const maxFiles = Math.min(files.length, maxNum);

			if (files.length > maxNum) {

			}

			for (let i = 0; i < maxFiles; i++) {
				const file = files[i] as File;
				const isVideo = file.type.startsWith('video/');
				const isImage = file.type.startsWith('image/');
				const fileType: 'video' | 'photo' = isVideo ? 'video' : 'photo';

				const reader = new FileReader();

				await new Promise<void>((resolveRead) => {
					reader.onload = (event: any) => {
						const dataUrl = event.target.result;
						const baseFields = {
							fileName: file.name,
							fileSize: file.size,
							file: file,
						};

						if (isImage) {
							const img = new Image();
							img.onload = () => {
								results.push({
									type: fileType,
									width: img.width || 0,
									height: img.height || 0,
									path: dataUrl,
									mimeType: file.type,
									isHadCompressed: false,
									...baseFields,
								});
								resolveRead();
							};
							img.onerror = () => {
								results.push({
									type: fileType,
									width: 0,
									height: 0,
									path: dataUrl,
									mimeType: file.type,
									isHadCompressed: false,
									...baseFields,
								});
								resolveRead();
							};
							img.src = dataUrl;
						} else if (isVideo) {
							// 视频文件：获取视频尺寸
							const video = document.createElement('video');
							video.preload = 'metadata';
							video.crossOrigin = 'anonymous';

							// 设置超时，避免无限等待
							const timeout = setTimeout(() => {
								console.warn('[openMultiplePicker] 视频元数据加载超时');
								video.onloadedmetadata = null;
								video.onerror = null;
								results.push({
									type: fileType,
									width: 0,
									height: 0,
									path: dataUrl,
									mimeType: file.type,
									isHadCompressed: false,
									...baseFields,
								});
								resolveRead();
							}, 10000); // 10秒超时

							video.onloadedmetadata = () => {
								clearTimeout(timeout);
								console.log('[openMultiplePicker] 视频加载成功，尺寸:', video.videoWidth, 'x', video.videoHeight);
								video.onloadedmetadata = null;
								video.onerror = null;
								results.push({
									type: fileType,
									width: video.videoWidth,
									height: video.videoHeight,
									path: dataUrl,
									mimeType: file.type,
									isHadCompressed: false,
									...baseFields,
								});
								resolveRead();
							};

							video.onerror = (error) => {
								clearTimeout(timeout);
								console.warn('[openMultiplePicker] 视频加载失败:', error);
								video.onloadedmetadata = null;
								video.onerror = null;
								results.push({
									type: fileType,
									width: 0,
									height: 0,
									path: dataUrl,
									mimeType: file.type,
									isHadCompressed: false,
									...baseFields,
								});
								resolveRead();
							};

							video.src = dataUrl;
						} else {
							// 其他文件类型
							let mimeType = file.type;
							if (!mimeType && file.name) {
								const ext = file.name.split('.').pop()?.toLowerCase();
								const mimeMap: Record<string, string> = {
									pdf: 'application/pdf',
									doc: 'application/msword',
									docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
									xls: 'application/vnd.ms-excel',
									xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
									ppt: 'application/vnd.ms-powerpoint',
									pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
									txt: 'text/plain',
									zip: 'application/zip',
									rar: 'application/x-rar-compressed',
									csv: 'text/csv',
									jpg: 'image/jpeg',
									jpeg: 'image/jpeg',
									png: 'image/png',
									gif: 'image/gif',
									webp: 'image/webp',
									svg: 'image/svg+xml',
								};
								mimeType = mimeMap[ext || ''] || 'application/octet-stream';
							}
							results.push({
								type: fileType,
								width: 0,
								height: 0,
								path: dataUrl,
								mimeType,
								isHadCompressed: false,
								...baseFields,
							});
							resolveRead();
						}
					};
					reader.onerror = () => resolveRead();
					reader.readAsDataURL(file);
				});
			}

			if (files.length > maxNum) {
				showMsgDialog(Str_MaxFileCountLimit);
			}

			resolve(results.length > 0 ? results : undefined);
		};

		input.oncancel = () => {
			resolve(undefined);
		};

		document.body.appendChild(input);
		input.click();

		setTimeout(() => {
			if (document.body.contains(input)) {
				document.body.removeChild(input);
			}
		}, 100);
	});
}

export async function openCamera(type: 'video' | 'photo'): Promise<PickerResult | undefined> {
	return openWebFilePicker(type);
}
